import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import ProductArchitectureFlow from '../diagrams/ProductArchitectureFlow';

/**
 * Product detail with architecture canvas (#product/:slug).
 */
export default function ProductDetailPage({ slug, content, onBack, onContact }) {
  const items = Array.isArray(content?.products?.items) ? content.products.items : [];
  const meta = items.find((p) => p.slug === slug);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const path = meta?.contentPath || `/products/${slug}.json`;
    setLoading(true);
    fetch(`${process.env.PUBLIC_URL || ''}${path}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => {
        if (!cancelled) setDetail(json);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, meta?.contentPath]);

  if (loading) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 px-4">
        <p className="font-body text-text-sub max-w-5xl mx-auto">Loading…</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 px-4">
        <div className="max-w-5xl mx-auto">
          <button type="button" onClick={onBack} className="text-cyan mb-6 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <p className="font-body text-text-sub">Product not found.</p>
        </div>
      </div>
    );
  }

  const features = Array.isArray(detail.features) ? detail.features : [];
  const highlights = Array.isArray(detail.highlights) ? detail.highlights : [];
  const clouds = Array.isArray(detail.clouds) ? detail.clouds : [];

  return (
    <div className="min-h-screen bg-forest-bg relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 0%, var(--jb-glow-cyan) 0%, transparent 70%), radial-gradient(ellipse 40% 30% at 90% 80%, var(--jb-glow-green) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-10 transition text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        <header className="mb-10 max-w-3xl">
          {detail.eyebrow && (
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-3">{detail.eyebrow}</p>
          )}
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-text-hero mb-3 leading-tight">
            {detail.title}
          </h1>
          {detail.tagline && (
            <p className="font-display font-semibold text-lg sm:text-xl text-cyan mb-5">{detail.tagline}</p>
          )}
          {detail.introduction && (
            <p className="font-body text-lg text-text-body leading-relaxed">{detail.introduction}</p>
          )}
        </header>

        {/* Architecture canvas — primary visual */}
        <section className="mb-12">
          <ProductArchitectureFlow slug={slug} />
        </section>

        {highlights.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            {highlights.map((h) => (
              <div
                key={h.label}
                className="p-5 rounded-xl border bg-forest-card"
                style={{ borderColor: 'var(--jb-border)' }}
              >
                <div className="font-mono text-xs text-cyan uppercase tracking-wider mb-2">{h.label}</div>
                <p className="font-body text-sm text-text-sub leading-relaxed">{h.text}</p>
              </div>
            ))}
          </div>
        )}

        {detail.description && (
          <section className="mb-10 max-w-3xl">
            <h2 className="font-display font-extrabold text-xl text-text-hero mb-3">Overview</h2>
            <p className="font-body text-text-sub leading-relaxed">{detail.description}</p>
          </section>
        )}

        {clouds.length > 0 && (
          <section className="mb-10">
            <h2 className="font-display font-extrabold text-xl text-text-hero mb-4">Cloud coverage</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {clouds.map((c) => (
                <div
                  key={c.name}
                  className="p-4 rounded-xl border bg-forest-card text-center"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <div className="font-display font-bold text-text-hero">{c.name}</div>
                  <div className="font-mono text-[0.65rem] uppercase tracking-wider text-cyan mt-1">
                    {c.status}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {features.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display font-extrabold text-xl text-text-hero mb-4">Capabilities</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {features.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 p-4 rounded-xl border bg-forest-card"
                  style={{ borderColor: 'var(--jb-border)' }}
                >
                  <span
                    className="mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--jb-cyan-dim)' }}
                  >
                    <Check className="w-3.5 h-3.5 text-cyan" />
                  </span>
                  <span className="font-body text-sm text-text-body leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section
          className="p-8 rounded-2xl border text-center"
          style={{ background: 'var(--jb-connect-bg)', borderColor: 'var(--jb-connect-border)' }}
        >
          <h2 className="font-display font-black text-xl sm:text-2xl text-text-hero mb-2">
            {detail.ctaText ?? `Talk about ${detail.title}`}
          </h2>
          <p className="font-body text-text-sub text-sm mb-6 max-w-md mx-auto">
            Book a discovery call to see how {detail.title} fits your stack.
          </p>
          <button
            type="button"
            onClick={onContact}
            className="jb-cta inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
          >
            Book a discovery call
            <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </div>
    </div>
  );
}
